import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../Utils/Axios';

const resolveHospitalLogo = (logo) => logo || '/assets/images/bg/company-logo/company-01.png';

const initialApplication = {
  applicantName: '',
  applicantEmail: '',
  phone: '',
  qualification: '',
  noticePeriod: '',
  gender: '',
  coverLetter: '',
  resume: null,
};

const JobDetails = () => {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showApplyForm, setShowApplyForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [application, setApplication] = useState(initialApplication);

  useEffect(() => {
    const loadJob = async () => {
      try {
        const res = await api.get(`/api/hospital/public-jobs/${id}`);
        setJob(res.data?.job || null);
      } catch (error) {
        setJob(null);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadJob();
    } else {
      setLoading(false);
    }
  }, [id]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'resume') {
      setApplication((prev) => ({ ...prev, resume: files?.[0] || null }));
      return;
    }
    setApplication((prev) => ({ ...prev, [name]: value }));
  };

  const handleApply = async (e) => {
    e.preventDefault();
    if (!application.resume) {
      toast.error('Resume is required');
      return;
    }

    const formData = new FormData();
    formData.append('applicantName', application.applicantName);
    formData.append('applicantEmail', application.applicantEmail);
    formData.append('phone', application.phone);
    formData.append('qualification', application.qualification);
    formData.append('noticePeriod', application.noticePeriod);
    formData.append('gender', application.gender);
    formData.append('coverLetter', application.coverLetter);
    formData.append('resume', application.resume);

    setSubmitting(true);
    try {
      const res = await api.post(`/api/hospital/public-jobs/${id}/apply`, formData);
      toast.success(res.data?.message || 'Application submitted');
      setApplication(initialApplication);
      setShowApplyForm(false);
    } catch (error) {
      const firstValidationError = error?.response?.data?.errors?.[0]?.message;
      toast.error(firstValidationError || error?.response?.data?.message || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="pt-120 mb-120 text-center">Loading job details...</div>;
  }

  if (!job) {
    return (
      <div className="pt-120 mb-120 text-center">
        <h4>Job not found.</h4>
      </div>
    );
  }

  return (
    <div>
      <div className="inner-banner">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="banner-content text-center">
                <h1>Job Details</h1>
                <span />
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/">Home</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to="/job-listing">Job Listing</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Job Details
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="job-details-pages pt-120 mb-120">
        <div className="container">
          <div className="row g-lg-4 gy-5">
            <div className="col-lg-8">
              <div className="job-details-content">
                <div className="job-list-content">
                  <div className="company-area">
                    <div className="logo">
                      <img src={resolveHospitalLogo(job.hospitalLogo)} alt={job.hospitalName || 'Hospital'} />
                    </div>
                    <div className="company-details">
                      <div className="name-location">
                        <h5>{job.title}</h5>
                        <p>{job.hospitalName}</p>
                      </div>
                    </div>
                  </div>
                  <div className="job-discription">
                    <ul className="one">
                      <li>
                        <img src="/assets/images/icon/map-2.svg" alt="" />
                        <p>
                          <span className="title">Location:</span>{" "}
                          {job.location || [job.city, job.state].filter(Boolean).join(", ") || "Maryland, USA"}
                        </p>
                      </li>
                        <li>
                          <img src="/assets/images/icon/category-2.svg" alt="" />
                          <p>
                            <span className="title">Category:</span> {job.category || '-'}
                          </p>
                        </li>
                        {job.subcategory ? (
                          <li>
                            <img src="/assets/images/icon/category-2.svg" alt="" />
                            <p>
                              <span className="title">Subcategory:</span> {job.subcategory}
                            </p>
                          </li>
                        ) : null}
                    </ul>
                    <ul>
                      <li>
                        <img src="/assets/images/icon/company-2.svg" alt="" />
                        <p>
                          <span className="title">Job Type:</span> {job.employmentType || '-'}
                        </p>
                      </li>
                      <li>
                        <img src="/assets/images/icon/salary-2.svg" alt="" />
                        <p>
                          <span className="title">Salary:</span> ${job.salaryMin || 0} - ${job.salaryMax || 0}
                        </p>
                      </li>
                    </ul>
                  </div>
                </div>

                <p>
                  <span>Job Description:</span> {job.description || 'No description provided.'}
                </p>
                <br />
                <p>
                  <span>Requirements:</span> {job.requirements || 'No specific requirements listed.'}
                </p>
              </div>
            </div>

            <div className="col-lg-4">
              <div className="job-details-sidebar mb-120">
                <div className="save-apply-btn d-flex justify-content-end mb-50">
                  <ul>
                    <li>
                      <button
                        type="button"
                        className="primry-btn-2 lg-btn"
                        onClick={() => setShowApplyForm(true)}
                      >
                        Apply Position
                      </button>
                    </li>
                  </ul>
                </div>
                <div className="job-summary-area mb-50">
                  <div className="job-summary-title">
                    <h6>Job Summary:</h6>
                  </div>
                  <ul>
                    <li>
                      <p>
                        <span className="title">Hospital:</span> {job.hospitalName}
                      </p>
                    </li>
                    <li>
                      <p>
                        <span className="title">Posted:</span> {new Date(job.createdAt).toLocaleDateString()}
                      </p>
                    </li>
                    <li>
                      <p>
                        <span className="title">Deadline:</span>{' '}
                        {job.deadline ? new Date(job.deadline).toLocaleDateString() : '-'}
                      </p>
                    </li>
                    <li>
                      <p>
                        <span className="title">ZIP:</span> {job.zip || '-'}
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {showApplyForm && (
            <div
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.55)',
                zIndex: 9999,
                overflowY: 'auto',
                padding: '30px 15px',
              }}
            >
              <div className="container">
                <div className="row justify-content-center">
                  <div className="col-lg-10">
                    <div className="form-wrapper" style={{ position: 'relative' }}>
                      <button
                        type="button"
                        onClick={() => setShowApplyForm(false)}
                        style={{
                          position: 'absolute',
                          right: 20,
                          top: 15,
                          border: 'none',
                          background: 'transparent',
                          fontSize: 24,
                          lineHeight: 1,
                        }}
                        aria-label="Close apply form"
                      >
                        x
                      </button>
                      <form onSubmit={handleApply}>
                        <div className="row">
                          <div className="col-lg-12">
                            <div className="section-title2">
                              <h5>Apply For This Position</h5>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-inner mb-25">
                              <label>Name*</label>
                              <input
                                type="text"
                                name="applicantName"
                                value={application.applicantName}
                                onChange={handleChange}
                                required
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-inner mb-25">
                              <label>Email*</label>
                              <input
                                type="email"
                                name="applicantEmail"
                                value={application.applicantEmail}
                                onChange={handleChange}
                                required
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-inner mb-25">
                              <label>Phone</label>
                              <input type="text" name="phone" value={application.phone} onChange={handleChange} />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-inner mb-25">
                              <label>Qualification*</label>
                              <input
                                type="text"
                                name="qualification"
                                value={application.qualification}
                                onChange={handleChange}
                                required
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-inner mb-25">
                              <label>Notice Period*</label>
                              <input
                                type="text"
                                name="noticePeriod"
                                value={application.noticePeriod}
                                onChange={handleChange}
                                placeholder="e.g. Immediate / 15 days / 30 days"
                                required
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-inner mb-25">
                              <label>Gender*</label>
                              <select name="gender" value={application.gender} onChange={handleChange} required>
                                <option value="">Select gender</option>
                                <option value="Male">Male</option>
                                <option value="Female">Female</option>
                                <option value="Other">Other</option>
                                <option value="Prefer not to say">Prefer not to say</option>
                              </select>
                            </div>
                          </div>
                          <div className="col-md-12">
                            <div className="form-inner mb-25">
                              <label>Resume (PDF/DOC/DOCX)*</label>
                              <input
                                type="file"
                                name="resume"
                                accept=".pdf,.doc,.docx"
                                onChange={handleChange}
                                required
                              />
                            </div>
                          </div>
                          <div className="col-md-12">
                            <div className="form-inner mb-30">
                              <label>Cover Letter</label>
                              <textarea
                                name="coverLetter"
                                rows={5}
                                value={application.coverLetter}
                                onChange={handleChange}
                                placeholder="Introduce yourself and explain why you are a strong fit."
                              />
                            </div>
                          </div>
                          <div className="col-md-12 d-flex gap-3">
                            <div className="form-inner">
                              <button className="primry-btn-2 lg-btn w-unset" type="submit" disabled={submitting}>
                                {submitting ? 'Submitting...' : 'Submit Application'}
                              </button>
                            </div>
                            <div className="form-inner">
                              <button type="button" className="primry-btn-2 lg-btn w-unset" onClick={() => setShowApplyForm(false)}>
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobDetails;

